/**
 * The kept plan and the two answers to it, as `/plans/nowy` draws them inside the page's own notice:
 * the card, the price of deleting it, „Usuń program" and „Przywróć niezapisany plan".
 *
 * The notice chrome — the centred column, the heading, the sentence — is the page's
 * (`BuilderNotice`); this component supplies only what is the feature's. „Usuń program" is drawn as
 * the destructive answer it is and asks no second question: the line naming its price is read with
 * it (`aria-describedby`), the treatment „Zacznij od nowa" gets in the dialog.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { LeftoverPlan } from '~/entities/plan-draft';
import surface from '~/shared/ui/dialogSurface.module.css';

import { LeftoverPlanCard } from './LeftoverPlanCard';
import card from './LeftoverPlanCard.module.css';
import styles from './LeftoverPlanAnswers.module.css';

export interface LeftoverPlanAnswersProps {
  readonly plan: LeftoverPlan;
  readonly onRestore: () => void;
  /** Deletes the plan. The caller moves focus first: this button is gone the moment it acts. */
  readonly onRemove: () => void;
}

export function LeftoverPlanAnswers({ plan, onRestore, onRemove }: LeftoverPlanAnswersProps) {
  const { t } = useTranslation();
  const cardId = useId();
  const noteId = useId();

  return (
    <div className={styles.answers}>
      <div className={styles.card}>
        <LeftoverPlanCard plan={plan} id={cardId} />
      </div>
      <p id={noteId} className={card.priceNote}>
        {t('web:planResume.deleteNote')}
      </p>
      <div className={styles.actions}>
        <button
          type="button"
          className={surface.dangerGhost}
          aria-describedby={`${noteId} ${cardId}`}
          onClick={onRemove}
        >
          {t('web:planResume.delete')}
        </button>
        <button
          type="button"
          className={surface.primary}
          aria-describedby={cardId}
          onClick={onRestore}
        >
          {t('web:planResume.restore')}
        </button>
      </div>
    </div>
  );
}
